import { useState, useEffect } from 'react';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { ScanTool } from '../scan/ToolSelector';
import { BASE_SCAN_TYPES, TOOL_BADGE, deriveSelectedTools } from '../scan/scanTypes';
import { ScanType } from '../../types';

export type ScheduleFrequency = 'once' | 'daily' | 'weekly' | 'monthly';

export interface Schedule {
  scheduleId: string;
  siteId: string;
  frequency: ScheduleFrequency;
  scheduledDate?: string; // ISO date for 'once' frequency
  dayOfWeek?: number; // 0-6 for weekly
  dayOfMonth?: number; // 1-28 for monthly
  hour: number; // 0-23
  minute: number; // 0-59
  timezone: string;
  pageLimitOverride?: number;
  tools: ScanTool[];
  scanTypes?: ScanType[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleFormData {
  frequency: ScheduleFrequency;
  scheduledDate?: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  hour: number;
  minute: number;
  timezone: string;
  pageLimitOverride?: number;
  tools: ScanTool[];
  scanTypes: ScanType[];
}

interface ScheduleFormProps {
  siteId: string;
  schedule?: Schedule;
  onSubmit: (data: ScheduleFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /**
   * Retained so callers need not change. Nothing here is gated on Search Console any
   * more: the outbound toxic-link check does not use it.
   */
  gscConnected?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function getTodayDate(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

export function ScheduleForm({
  siteId: _siteId,
  schedule,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: ScheduleFormProps) {
  const isEditing = !!schedule;
  const SCAN_TYPES = BASE_SCAN_TYPES;

  // Form state
  const [frequency, setFrequency] = useState<ScheduleFrequency>(
    schedule?.frequency ?? 'weekly'
  );
  const [scheduledDate, setScheduledDate] = useState<string>(
    schedule?.scheduledDate ?? getTodayDate()
  );
  const [dayOfWeek, setDayOfWeek] = useState<number>(
    schedule?.dayOfWeek ?? 1 // Monday
  );
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    schedule?.dayOfMonth ?? 1
  );
  const [hour, setHour] = useState<number>(schedule?.hour ?? 9);
  const [minute, setMinute] = useState<number>(schedule?.minute ?? 0);
  const [timezone, setTimezone] = useState<string>(
    schedule?.timezone ?? getDefaultTimezone()
  );
  const [pageLimitOverride, setPageLimitOverride] = useState<string>(
    schedule?.pageLimitOverride === 10000 ? '' : (schedule?.pageLimitOverride?.toString() ?? '')
  );
  const [scanAllPages, setScanAllPages] = useState<boolean>(
    schedule?.pageLimitOverride === 10000
  );
  const [scanTypes, setScanTypes] = useState<ScanType[]>(
    schedule?.scanTypes ?? ['accessibility', 'eaa', 'seo', 'geo', 'performance', 'brokenLinks']
  );
  // Engines follow the scan types, as they do in the New Scan modal — a scheduled scan
  // runs through the same scanner, so it must not be able to ask for a different set.
  // A schedule runs unattended, so accessibility is always the full run.
  const tools: ScanTool[] = deriveSelectedTools(scanTypes, 'full');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Check if the selected timezone is in the common list
  const isCustomTimezone =
    timezone && !COMMON_TIMEZONES.some((tz) => tz.value === timezone);

  // Add the user's timezone to the list if it's not already there
  const timezoneOptions = isCustomTimezone
    ? [{ value: timezone, label: timezone }, ...COMMON_TIMEZONES]
    : COMMON_TIMEZONES;

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!frequency) {
      newErrors.frequency = 'Please select a frequency';
    }

    if (frequency === 'once' && !scheduledDate) {
      newErrors.scheduledDate = 'Please select a date';
    }

    if (frequency === 'once' && scheduledDate) {
      const selected = new Date(scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selected < today) {
        newErrors.scheduledDate = 'Date must be today or in the future';
      }
    }

    if (scanTypes.length === 0) {
      newErrors.scanTypes = 'Please select at least one scan type';
    }

    if (pageLimitOverride && (parseInt(pageLimitOverride) < 1 || parseInt(pageLimitOverride) > 1000)) {
      newErrors.pageLimitOverride = 'Page limit must be between 1 and 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const data: ScheduleFormData = {
      frequency,
      hour,
      minute,
      timezone,
      tools,
      scanTypes,
    };

    if (frequency === 'once') {
      data.scheduledDate = scheduledDate;
    } else if (frequency === 'weekly') {
      data.dayOfWeek = dayOfWeek;
    } else if (frequency === 'monthly') {
      data.dayOfMonth = dayOfMonth;
    }

    if (scanAllPages) {
      data.pageLimitOverride = 10000;
    } else if (pageLimitOverride) {
      data.pageLimitOverride = parseInt(pageLimitOverride);
    }

    onSubmit(data);
  };

  // Clear relevant errors when values change
  useEffect(() => {
    if (errors.frequency && frequency) {
      setErrors((prev) => ({ ...prev, frequency: '' }));
    }
  }, [frequency, errors.frequency]);

  useEffect(() => {
    if (errors.scheduledDate && scheduledDate) {
      setErrors((prev) => ({ ...prev, scheduledDate: '' }));
    }
  }, [scheduledDate, errors.scheduledDate]);

  useEffect(() => {
    if (errors.scanTypes && scanTypes.length > 0) {
      setErrors((prev) => ({ ...prev, scanTypes: '' }));
    }
  }, [scanTypes, errors.scanTypes]);

  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i.toString().padStart(2, '0'),
  }));

  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = [
    { value: 0, label: '00' },
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
  ];

  // Generate day of month options (1-28)
  const dayOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString(),
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Frequency Selection */}
      <div>
        <label className="block text-body font-medium mb-3">
          Frequency <span className="text-coral">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(['once', 'daily', 'weekly', 'monthly'] as ScheduleFrequency[]).map(
            (freq) => (
              <label
                key={freq}
                className={`flex items-center justify-center p-3 rounded-card border cursor-pointer transition-colors
                  ${
                    frequency === freq
                      ? 'border-coral bg-coral/5 text-coral'
                      : 'border-gray-light hover:border-coral'
                  }
                  ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name="frequency"
                  value={freq}
                  checked={frequency === freq}
                  onChange={() => setFrequency(freq)}
                  disabled={isSubmitting}
                  className="sr-only"
                />
                <span className="font-medium capitalize">{freq}</span>
              </label>
            )
          )}
        </div>
        {errors.frequency && (
          <div className="flex items-center gap-2 mt-2 text-status-error text-body-sm">
            <AlertCircle size={14} />
            <span>{errors.frequency}</span>
          </div>
        )}
      </div>

      {/* Date Picker (for Once) */}
      {frequency === 'once' && (
        <div>
          <label className="block text-body font-medium mb-2">
            <span className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-medium" />
              Date <span className="text-coral">*</span>
            </span>
          </label>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            min={getTodayDate()}
            disabled={isSubmitting}
            className="input w-full sm:w-auto"
          />
          {errors.scheduledDate && (
            <div className="flex items-center gap-2 mt-2 text-status-error text-body-sm">
              <AlertCircle size={14} />
              <span>{errors.scheduledDate}</span>
            </div>
          )}
        </div>
      )}

      {/* Day of Week (for Weekly) */}
      {frequency === 'weekly' && (
        <div>
          <label className="block text-body font-medium mb-2">
            Day of Week <span className="text-coral">*</span>
          </label>
          <select
            value={dayOfWeek}
            onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
            disabled={isSubmitting}
            className="input w-full sm:w-auto"
          >
            {DAYS_OF_WEEK.map((day) => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Day of Month (for Monthly) */}
      {frequency === 'monthly' && (
        <div>
          <label className="block text-body font-medium mb-2">
            Day of Month <span className="text-coral">*</span>
          </label>
          <select
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
            disabled={isSubmitting}
            className="input w-full sm:w-auto"
          >
            {dayOfMonthOptions.map((day) => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
          <p className="text-caption text-gray-medium mt-1">
            Limited to 1-28 to ensure the day exists in all months
          </p>
        </div>
      )}

      {/* Time Picker */}
      <div>
        <label className="block text-body font-medium mb-2">
          <span className="flex items-center gap-2">
            <Clock size={18} className="text-gray-medium" />
            Time <span className="text-coral">*</span>
          </span>
        </label>
        <div className="flex items-center gap-2">
          <select
            value={hour}
            onChange={(e) => setHour(parseInt(e.target.value))}
            disabled={isSubmitting}
            className="input w-20"
            aria-label="Hour"
          >
            {hourOptions.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>
          <span className="text-body font-medium">:</span>
          <select
            value={minute}
            onChange={(e) => setMinute(parseInt(e.target.value))}
            disabled={isSubmitting}
            className="input w-20"
            aria-label="Minute"
          >
            {minuteOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timezone Selector */}
      <div>
        <label className="block text-body font-medium mb-2">Timezone</label>
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={isSubmitting}
          className="input w-full sm:w-auto"
        >
          {timezoneOptions.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>

      {/* Page Limit Override */}
      <div>
        <label className="block text-body font-medium mb-2">
          Page Limit Override
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={1}
            max={1000}
            value={pageLimitOverride}
            onChange={(e) => setPageLimitOverride(e.target.value)}
            placeholder="Use site default"
            disabled={isSubmitting || scanAllPages}
            className="input w-32"
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={scanAllPages}
              onChange={(e) => {
                setScanAllPages(e.target.checked);
                if (e.target.checked) setPageLimitOverride('');
              }}
              disabled={isSubmitting}
              className="h-4 w-4 text-coral rounded focus:ring-coral"
            />
            <span className="text-body">Scan all pages</span>
          </label>
        </div>
        <p className="text-caption text-gray-medium mt-1">
          {scanAllPages ? 'All discovered pages will be scanned' : 'Leave empty to use the site\'s default page limit (1-1000)'}
        </p>
        {errors.pageLimitOverride && (
          <div className="flex items-center gap-2 mt-2 text-status-error text-body-sm">
            <AlertCircle size={14} />
            <span>{errors.pageLimitOverride}</span>
          </div>
        )}
      </div>

      {/* Scan Types */}
      <div>
        <label className="block text-body font-medium mb-3">
          Scan Types <span className="text-coral">*</span>
        </label>
        <div className="space-y-2">
          {SCAN_TYPES.map(type => (
            <label
              key={type.value}
              className={`flex items-start gap-3 p-3 rounded-card border cursor-pointer transition-colors
                ${scanTypes.includes(type.value) ? 'border-coral bg-coral/5' : 'border-gray-light hover:border-coral'}
                ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={scanTypes.includes(type.value)}
                onChange={() => {
                  setScanTypes(prev =>
                    prev.includes(type.value)
                      ? prev.filter(t => t !== type.value)
                      : [...prev, type.value]
                  );
                }}
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 text-coral rounded focus:ring-coral"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{type.label}</span>
                  {type.tools.map(t => (
                    <span
                      key={t}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-caption font-medium ${TOOL_BADGE[t].className}`}
                    >
                      {TOOL_BADGE[t].label}
                    </span>
                  ))}
                </div>
                <div className="text-body-sm text-gray-medium">{type.description}</div>
              </div>
            </label>
          ))}
        </div>
        {errors.scanTypes && (
          <div className="flex items-center gap-2 mt-2 text-status-error text-body-sm">
            <AlertCircle size={14} />
            <span>{errors.scanTypes}</span>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-light">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn-secondary flex-1"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary flex-1"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              {isEditing ? 'Updating...' : 'Creating...'}
            </span>
          ) : isEditing ? (
            'Update Schedule'
          ) : (
            'Create Schedule'
          )}
        </button>
      </div>
    </form>
  );
}
